import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UsersRound, Pencil, Check, Pause, Play, UserCog, X, DoorOpen } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Section, Loadable, Empty, Status, Info, History, ActionButton, ActionDialog, RunButton, BackLink, Tabbar, TabPanel, useTabs, useLoad, fmt,
  PageView, PageRows, PageButtons, errText,
} from "@/components/control/kit";
import { toast } from "sonner";
import { categoryLabel } from "@/components/control/activityFields";

function Rules({ g, canManage, reload }) {
  return (
    <Section title={`Gruppenregeln${g.rules_version ? ` (Version ${g.rules_version})` : ""}`} testId="group-rules" actions={canManage && (
      <ActionButton label="Regeln bearbeiten" icon={Pencil} testId="edit-rules" onDone={reload} submitLabel="Zur Freigabe senden"
        page="regeln" title="Gruppenregeln bearbeiten" pageSubtitle="Neue Regeln werden als prüfbarer Entwurf mit Versionshinweis geführt." pageSubject={g.name_de}
        pageRows={[["Kreis", g.name_de], ["Aktuelle Version", g.rules_version ? `Version ${g.rules_version}` : "Noch keine freigegebene Version"],
          ["Offener Vorschlag", g.pending_rules ? `von ${g.pending_rules.by.name} · ${fmt(g.pending_rules.at)}` : "keiner"]]}
        description="Änderungen werden erst nach Freigabe durch eine zweite Person wirksam."
        fields={[
          { name: "rules", label: "Regeln (eine pro Zeile)", type: "textarea", rows: 6, required: true, default: (g.rules || []).join("\n") },
          { name: "reason", label: "Grund der Änderung", type: "textarea", required: true },
        ]}
        onSubmit={async (v) => { await api.put(`/manage/groups/${g.id}/rules`, { rules: v.rules.split("\n"), reason: v.reason }); return "Regeländerung zur Freigabe gesendet."; }} />
    )}>
      {(g.rules || []).length === 0 ? <Empty text="Noch keine Regeln festgelegt." /> : (
        <ol className="list-decimal space-y-1 pl-5 text-sm">{g.rules.map((r, i) => <li key={i}>{r}</li>)}</ol>
      )}
      {g.pending_rules && (
        <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-3" data-testid="pending-rules">
          <p className="text-sm font-semibold text-orange-800">Vorschlag von {g.pending_rules.by.name} · {fmt(g.pending_rules.at)}</p>
          <ol className="mt-1 list-decimal pl-5 text-sm text-orange-900">{g.pending_rules.rules.map((r, i) => <li key={i}>{r}</li>)}</ol>
          <p className="mt-1 text-xs text-orange-800">Grund: {g.pending_rules.reason}</p>
          {canManage && <div className="mt-2"><RunButton label="Regeln freigeben" icon={Check} testId="approve-rules" success="Regeln freigegeben." onDone={reload}
            run={() => api.post(`/manage/groups/${g.id}/rules/approve`)} /></div>}
        </div>
      )}
    </Section>
  );
}

// Works only on the snapshot taken when the dialog opens: a reload while it is open never rebinds version or values.
function GroupTextEditor({ g, reload }) {
  const [snap, setSnap] = useState(null);
  const submit = async (v) => {
    const body = { version: snap.version, reason: v.reason };
    if (v.name_de !== snap.name_de) body.name_de = v.name_de;
    if (v.description_de !== snap.description_de) body.description_de = v.description_de;
    await api.patch(`/manage/groups/${snap.id}`, body);
    return "Name und Beschreibung gespeichert.";
  };
  return (
    <>
      <Button type="button" variant="outline" size="sm" className="gap-1.5" data-testid="edit-group-text"
        onClick={() => setSnap({ id: g.id, ...g.text_edit })}>
        <Pencil className="h-4 w-4" aria-hidden="true" />Name & Beschreibung
      </Button>
      <ActionDialog open={snap !== null} onOpenChange={(open) => { if (!open) setSnap(null); }} testId="edit-group-text-dialog"
        title="Name und Beschreibung bearbeiten" submitLabel="Speichern" onSubmit={submit} onDone={reload}
        description="Geändert wird nur die deutsche Fassung. Andere Sprachen, Mitgliedschaften, Regeln und Verantwortung bleiben unverändert."
        fields={snap ? [
          { name: "name_de", label: "Name (deutsch)", required: true, default: snap.name_de, help: "Höchstens 80 Zeichen, eine Zeile." },
          { name: "description_de", label: "Beschreibung (deutsch)", type: "textarea", rows: 5, max: 1000, default: snap.description_de,
            help: "Optional. Wer das Feld leert, entfernt die deutsche Beschreibung; andere Sprachen bleiben." },
          { name: "reason", label: "Begründung (intern)", type: "textarea", required: true, max: 500 },
        ] : []} />
    </>
  );
}

function Actions({ d, reload }) {
  const g = d.group;
  if (!d.can_manage) return <Status value={g.status} />;
  return (
    <>
      <Status value={g.status} testId="group-status" />
      <GroupTextEditor g={g} reload={reload} />
      {g.status === "pausiert" ? (
        <ActionButton label="Fortsetzen" icon={Play} testId="resume-group" onDone={reload} submitLabel="Fortsetzen"
          page="fortsetzen" title="Gruppe fortsetzen" pageSubtitle="Prüfe Grund und Auswirkungen, bevor der Kreis wieder aktiv wird."
          pageSubject={g.name_de} pageRows={[["Kreis", g.name_de], ["Aktueller Status", "Pausiert"]]}
          fields={[{ name: "reason", label: "Grund", type: "textarea", required: true }]}
          onSubmit={async (v) => { await api.post(`/manage/groups/${g.id}/resume`, v); return "Kreis fortgesetzt."; }} />
      ) : (
        <ActionButton label="Pausieren" icon={Pause} danger testId="pause-group" onDone={reload} title="Gruppe pausieren bestätigen" submitLabel="Kreispause bestätigen"
          page="pausieren" pageSubtitle="Prüfe Dauer und Auswirkungen der Kreispause." pageSubject={g.name_de}
          pageRows={[["Kreis", g.name_de], ["Mitglieder", `${d.members.length} Person(en)`]]}
          pageNotice={{ title: "Deine Bestätigung ist erforderlich", text: "Prüfe den angezeigten Vorgang und die konkreten Auswirkungen." }}
          description="Während der Pause sind keine neuen Beitritte möglich."
          fields={[
            { name: "days", label: "Dauer", type: "select", required: true, default: "7", placeholder: false,
              options: [1, 3, 7, 14, 30].map((x) => ({ value: String(x), label: `${x} Tage` })) },
            { name: "reason", label: "Grund (intern)", type: "textarea", required: true },
            { name: "message", label: "Nachricht an die Mitglieder (optional)", type: "textarea" },
          ]}
          onSubmit={async (v) => { const { data } = await api.post(`/manage/groups/${g.id}/pause`, { ...v, days: Number(v.days) }); return `Kreis pausiert. ${data.notified} benachrichtigt.`; }} />
      )}
      <ActionButton label="Verantwortung übertragen" icon={UserCog} testId="transfer-group" onDone={reload} submitLabel="Übertragung anfragen"
        page="uebertragen" title="Gruppenverantwortung übertragen" pageSubtitle="Die neue Kreisverantwortung wird erst nach bewusster Zustimmung wirksam."
        pageSubject={g.name_de} pageRows={[["Kreis", g.name_de], ["Bisherige Verantwortung", g.owner?.name || "nicht festgelegt"]]}
        description="Die Person muss in der Mitglieder-App zustimmen, bevor die Übertragung wirksam wird."
        fields={[
          { name: "member_id", label: "Neue verantwortliche Person", type: "select", required: true, options: d.members.map((m) => ({ value: m.id, label: m.name })) },
          { name: "reason", label: "Begründung", type: "textarea", required: true },
        ]}
        onSubmit={async (v) => { await api.post(`/manage/groups/${g.id}/transfer`, v); return "Übertragung angefragt – Zustimmung ausstehend."; }} />
    </>
  );
}

function RequestPage({ g, r, reload }) {
  const [busy, setBusy] = useState(false);
  const approve = async (back, navigate) => {
    setBusy(true);
    try {
      await api.post(`/manage/groups/${g.id}/requests/${r.membership_id}/decide`, { approve: true });
      toast.success("Aufnahme bestätigt.");
      reload();
      navigate(back, { replace: true });
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };
  return (
    <PageView page={`pruefen-${r.membership_id}`} label="Prüfen" testId={`review-request-${r.membership_id}`} title="Aufnahmeanfrage prüfen"
      subtitle="Prüfe den freiwilligen Gruss und die bestätigten Kreisregeln." subject={r.name}>
      {({ back, navigate }) => (
        <>
          <div className="rounded-2xl border border-[#E6E1F0] bg-white p-4" data-testid="request-person">
            <p className="text-xl font-bold text-brand-purple">{r.name}</p>
            <span className="mt-2 inline-flex rounded-full border border-amber-200 bg-[#FFF6DD] px-2.5 py-0.5 text-xs font-semibold text-[#8A5A00]">Anfrage offen</span>
          </div>
          <PageRows testId="request" rows={[
            ["Anfragende Person", r.name], ["Kreis", g.name_de], ["Angefragt am", fmt(r.requested_at)],
            ["Gruss", r.message ? `„${r.message}“` : "Kein Gruss (freiwillig)"],
            ["Interessen", r.interests === null ? "nur mit Mitglieder-Leserecht sichtbar" : (r.interests.join(", ") || "keine angegeben")],
            ["Regeln", g.rules_version ? `Kreisregeln Version ${g.rules_version}` : "Keine freigegebenen Kreisregeln"]]} />
          <PageButtons primary="Aufnahme bestätigen" busy={busy} onPrimary={() => approve(back, navigate)} back={back} secondary="Abbrechen"
            testId={`review-request-${r.membership_id}`} />
          <Link to={back.replace(/(\?|$)/, `/anfrage-${r.membership_id}$1`)} data-testid={`review-request-decline-${r.membership_id}`}
            className="inline-block text-sm font-semibold text-brand-magenta underline">Stattdessen ablehnen und begründen</Link>
        </>
      )}
    </PageView>
  );
}

function JoinRequests({ d, reload }) {
  const g = d.group;
  const base = `/manage/groups/${g.id}/requests`;
  return (
    <Section title={`Aufnahmeanfragen (${d.requests.length})`} testId="group-requests" actions={d.can_manage && (
      <ActionButton label="Beitrittsregel" icon={DoorOpen} testId="edit-join-policy" onDone={reload} submitLabel="Speichern"
        page="beitrittsregel" title="Beitrittsregel festlegen" pageSubtitle="Lege fest, ob neue Mitglieder direkt oder nach Bestätigung aufgenommen werden."
        description="Mit Bestätigung erhalten Mitglieder erst nach deiner Entscheidung Zugang zu Unterhaltung und Beiträgen des Kreises."
        fields={[
          { name: "policy", label: "Beitritt", type: "select", required: true, default: g.join_policy, placeholder: false,
            options: [{ value: "offen", label: "Offen – Beitritt sofort wirksam" }, { value: "bestaetigung", label: "Nur mit Bestätigung durch das Team" }] },
          { name: "reason", label: "Grund", type: "textarea", required: true },
        ]}
        onSubmit={async (v) => { await api.put(`/manage/groups/${g.id}/join-policy`, v); return "Beitrittsregel gespeichert."; }} />
    )}>
      <p className="mb-3 text-sm text-zinc-700" data-testid="group-join-policy">
        Beitritt: <strong>{g.join_policy === "bestaetigung" ? "nur mit Bestätigung" : "offen"}</strong>
      </p>
      {d.requests.length === 0 ? <Empty text="Keine offenen Anfragen." testId="group-requests-empty" /> : (
        <ul className="divide-y divide-border text-sm">
          {d.requests.map((r) => (
            <li key={r.membership_id} className="flex flex-wrap items-start justify-between gap-2 py-3" data-testid={`group-request-${r.membership_id}`}>
              <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                <p className="break-words">{r.name} <span className="text-xs text-muted-foreground">· {fmt(r.requested_at)}</span></p>
                <p className="mt-1 break-words text-xs text-zinc-700" data-testid={`group-request-interests-${r.membership_id}`}>
                  Interessen: {r.interests === null ? "nur mit Mitglieder-Leserecht sichtbar" : (r.interests.join(", ") || "keine angegeben")}
                </p>
                <p className="mt-1 break-words text-xs text-zinc-700" data-testid={`group-request-message-${r.membership_id}`}>
                  Grußnachricht: {r.message ? `„${r.message}“` : "keine (freiwillig)"}
                </p>
              </div>
              {d.can_manage && (
                <span className="flex flex-wrap gap-2">
                  <RequestPage g={g} r={r} reload={reload} />
                  <RunButton label="Aufnehmen" icon={Check} testId={`approve-request-${r.membership_id}`} success="Aufnahme bestätigt." onDone={reload}
                    run={() => api.post(`${base}/${r.membership_id}/decide`, { approve: true })} />
                  <ActionButton label="Ablehnen" icon={X} danger testId={`decline-request-${r.membership_id}`} onDone={reload} submitLabel="Ablehnen"
                    page={`anfrage-${r.membership_id}`} title="Aufnahmeanfrage ablehnen" pageSubtitle="Prüfe den freiwilligen Gruss und die bestätigten Kreisregeln."
                    fields={[
                      { name: "reason", label: "Grund (intern)", type: "textarea", required: true },
                      { name: "message", label: "Nachricht an die Person (optional)", type: "textarea" },
                    ]}
                    onSubmit={async (v) => { await api.post(`${base}/${r.membership_id}/decide`, { approve: false, ...v }); return "Anfrage abgelehnt."; }} />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Gemeldete Beiträge prüfst du unter{" "}
        <Link to="/community-beitraege" className="font-medium text-brand-purple underline">Community-Beiträge</Link> und{" "}
        <Link to="/meldungen-sicherheit" className="font-medium text-brand-purple underline">Meldungen & Sicherheit</Link>.</p>
    </Section>
  );
}

function MemberList({ members, limit, testId }) {
  const list = limit ? members.slice(0, limit) : members;
  return list.length === 0 ? <Empty text="Noch keine bestätigten Mitglieder." testId={`${testId}-empty`} /> : (
    <ul className="divide-y divide-border text-sm" data-testid={testId}>
      {list.map((m) => <li key={m.id} className="flex flex-wrap justify-between gap-2 py-2"><span className="break-words">{m.name}</span><span className="text-xs text-muted-foreground">beigetreten {fmt(m.joined_at)}</span></li>)}
    </ul>
  );
}

function OverviewTab({ d }) {
  const g = d.group;
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Section title="Über diesen Kreis" testId="group-info">
        <Info items={[["Mitglieder", d.members.length], ["Verantwortlich", g.owner?.name || "nicht festgelegt"],
          ["Beitritt", g.join_policy === "bestaetigung" ? "nur mit Bestätigung" : "offen"],
          g.pending_owner && ["Übertragung", `an ${g.pending_owner.name} – Zustimmung ausstehend`],
          g.paused_until && ["Pausiert bis", fmt(g.paused_until)], g.pause_reason && ["Pausengrund", g.pause_reason],
          ["Gruppenchat", "wird im Control OS nicht geöffnet; nur gemeldete Ausschnitte sind prüfbar"],
          ["Beschreibung", g.description_de]]} />
      </Section>
      <Section title="Letzte Mitglieder" testId="group-members-preview"><MemberList members={d.members} limit={5} testId="group-members-preview-list" /></Section>
    </div>
  );
}

function Meetings({ d }) {
  if (d.activities === null) {
    return <Section title="Treffen" testId="group-meetings"><Empty text="Deine Rolle hat keinen Zugriff auf Aktivitäten." testId="group-meetings-restricted" /></Section>;
  }
  return (
    <Section title={`Treffen (${d.activities.length})`} testId="group-meetings">
      {d.activities.length === 0 ? (
        <Empty text="Diesem Kreis ist noch keine Aktivität zugeordnet. Die Zuordnung erfolgt in der Aktivität über „Kreis zuordnen“." testId="group-meetings-empty" />
      ) : (
        <ul className="divide-y divide-border text-sm" data-testid="group-meetings-list">
          {d.activities.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <Link to={`/aktivitaeten/${a.id}`} className="min-w-0 break-words font-medium text-brand-purple hover:underline">{a.title_de}</Link>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">{fmt(a.date, a.timezone)} · {a.country} <Status value={a.status} /></span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Die Zuordnung ist informativ. Wer eine Aktivität in der Mitglieder-App sieht, ändert sich dadurch nicht.</p>
    </Section>
  );
}

function GroupView({ d, reload }) {
  const g = d.group;
  const { aktion = "" } = useParams();
  // Deep links to an action page open the tab that contains the action.
  const [tab, setTab] = useState(aktion === "regeln" ? "regeln"
    : aktion === "beitrittsregel" || ["anfrage-", "ablehnen-", "pruefen-"].some((p) => aktion.startsWith(p)) ? "anfragen" : "uebersicht");
  const tabs = useTabs([{ value: "uebersicht", label: "Übersicht" }, { value: "mitglieder", label: "Mitglieder", count: d.members.length },
    { value: "anfragen", label: "Anfragen", count: d.requests.length }, { value: "treffen", label: "Treffen" },
    { value: "regeln", label: "Regeln" }, { value: "verlauf", label: "Verlauf" }], tab);
  return (
    <>
      <PageHeader title={g.name_de} subtitle={`${categoryLabel(g.category_key)} · ${g.organization || "ohne Organisation"}`} icon={UsersRound}
        actions={<Actions d={d} reload={reload} />} />
      <Tabbar tabs={tabs} onChange={setTab} testId="group-tabs" ariaLabel="Bereiche des Kreises" />
      <TabPanel tabs={tabs} testId="group-tabs-panel">
        {tabs.value === "uebersicht" && <OverviewTab d={d} />}
        {tabs.value === "mitglieder" && <Section title="Mitglieder" testId="group-members"><MemberList members={d.members} testId="group-members-list" /></Section>}
        {tabs.value === "anfragen" && <JoinRequests d={d} reload={reload} />}
        {tabs.value === "treffen" && <Meetings d={d} />}
        {tabs.value === "regeln" && <Rules g={g} canManage={d.can_manage} reload={reload} />}
        {tabs.value === "verlauf" && <Section title="Verlauf" testId="group-history"><History items={g.history} /></Section>}
      </TabPanel>
    </>
  );
}

export default function GroupDetail() {
  const { id } = useParams();
  const st = useLoad(`/manage/groups/${id}`);
  return (
    <div>
      <BackLink to="/kreise-gruppen">Zurück zu Kreise & Gruppen</BackLink>
      <Loadable state={st}>{(d) => <GroupView key={d.group.id} d={d} reload={st.reload} />}</Loadable>
    </div>
  );
}
